/**
 * CritiqueModeContext - Context for critique mode session data
 *
 * Provides session ID and user role (photographer vs critic) to nested components
 * like templates without prop drilling through the component tree.
 *
 * Feature: 649-critique-feedback-options (Rating display in templates)
 *
 * @module contexts/CritiqueModeContext
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';

/**
 * Critique mode context value
 */
export interface CritiqueModeContextValue {
  /**
   * Global ID of the current critique session
   * null when not in critique mode
   */
  sessionId: string | null;

  /**
   * Whether the current user is the photographer (collection owner)
   * - true: User owns the collection being critiqued
   * - false: User is a critic (doesn't own the collection)
   */
  isPhotographer: boolean;

  /**
   * Whether the current session has rating feedback enabled
   */
  hasRatingFeedback: boolean;
}

/**
 * Default context value (not in critique mode)
 */
const defaultValue: CritiqueModeContextValue = {
  sessionId: null,
  isPhotographer: false,
  hasRatingFeedback: false,
};

/**
 * Context for critique mode data
 */
const CritiqueModeContext = createContext<CritiqueModeContextValue>(defaultValue);

/**
 * Provider props
 */
export interface CritiqueModeProviderProps {
  /** Global ID of the critique session */
  sessionId: string | null | undefined;
  /** Whether current user is the photographer (collection owner) */
  isPhotographer: boolean;
  /** Whether rating feedback is enabled for this session */
  hasRatingFeedback?: boolean;
  /** Children components */
  children: ReactNode;
}

/**
 * CritiqueModeProvider - Provides critique session context to descendants
 *
 * Wrap critique mode content with this provider to give templates access
 * to session data for rating display.
 *
 * @example
 * ```tsx
 * <CritiqueModeProvider
 *   sessionId={session?.globalId}
 *   isPhotographer={isCollectionOwner}
 *   hasRatingFeedback={session?.feedbackOptions?.hasRating}
 * >
 *   <CollectionAndDetailView2 ... />
 * </CritiqueModeProvider>
 * ```
 */
export function CritiqueModeProvider({
  sessionId,
  isPhotographer,
  hasRatingFeedback = false,
  children,
}: CritiqueModeProviderProps) {
  const value = useMemo<CritiqueModeContextValue>(
    () => ({
      sessionId: sessionId ?? null,
      isPhotographer,
      hasRatingFeedback,
    }),
    [sessionId, isPhotographer, hasRatingFeedback]
  );

  return (
    <CritiqueModeContext.Provider value={value}>
      {children}
    </CritiqueModeContext.Provider>
  );
}

/**
 * useCritiqueMode - Access critique mode context
 *
 * Returns session context for templates to display appropriate ratings.
 * Safe to call outside of CritiqueModeProvider - returns defaults.
 *
 * @returns CritiqueModeContextValue with session data
 *
 * @example
 * ```tsx
 * function MyTemplate() {
 *   const { sessionId, isPhotographer, hasRatingFeedback } = useCritiqueMode();
 *
 *   if (!sessionId || !hasRatingFeedback) {
 *     return null; // No rating display outside critique mode
 *   }
 *
 *   return isPhotographer
 *     ? <AverageRating photoId={photoId} sessionId={sessionId} />
 *     : <MyRating photoId={photoId} sessionId={sessionId} />;
 * }
 * ```
 */
export function useCritiqueMode(): CritiqueModeContextValue {
  return useContext(CritiqueModeContext);
}
